import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Paintbrush, Home as HomeIcon, Grid3x3, Star, MapPin, Phone, Mail, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services } from '../data/mockData';
import ReviewCarousel from '../components/ReviewCarousel';
import SEO from '../components/SEO';
import JsonLd from '../components/JsonLd';
import { localBusinessSchema } from '../lib/schema';

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className="min-h-screen">
      <SEO 
        title="Painter and Decorator in Kent | Paint It Better"
        description="Local painter and decorator in Kent specialising in high-quality interior and exterior work for homes and landlords. Get a detailed quote and 10/10 service."
        canonical="/"
      />
      <JsonLd data={localBusinessSchema()} />
      {/* Hero Section */}
      <section className="relative h-screen -mt-20 pt-20 flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/images/emergent/hero-background.jpg)'
          }}
        />
        {/* Overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/60 to-slate-900/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent" />
        
        <div className={`relative z-10 text-center px-4 max-w-5xl mx-auto transition-all duration-1000 transform ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-4 md:mb-6 tracking-tight leading-tight">
            High-quality decorating,
            <span className="block text-cyan-400">minimal disruption</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-slate-200 mb-6 md:mb-10 max-w-3xl mx-auto leading-relaxed">
            We help Kent homeowners, landlords and small businesses refresh their properties with careful preparation, tidy workmanship and reliable timelines – plus daily photo and video updates with commentary when you can't be on site.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
            <Link to="/services">
              <Button size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-white px-6 md:px-8 py-4 md:py-6 text-base md:text-lg rounded-full transition-all duration-300 hover:scale-105 shadow-xl border-2 border-cyan-400 w-full sm:w-auto">
                View our services
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a href="https://www.checkatrade.com/trades/PaintItBetterLtd" target="_blank" rel="noopener">
              <Button size="lg" variant="outline" className="border-2 border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-slate-900 px-6 md:px-8 py-4 md:py-6 text-base md:text-lg rounded-full transition-all duration-300 hover:scale-105 shadow-xl w-full sm:w-auto">
                See our Checkatrade reviews
              </Button>
            </a>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce hidden md:block">
          <div className="w-6 h-10 border-2 border-cyan-400 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-cyan-400 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 bg-gradient-to-r from-cyan-50 to-teal-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Trusted decorating, without the drama
            </h2>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
              Homeowners, landlords and local businesses across Kent trust us with their keys, their timelines and their homes. We focus on one job at a time, work quickly without cutting corners, and keep you updated with photo and video progress reports until everything's finished.
            </p>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              What We Can Do For You
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Professional painting and decorating for homes, landlords and local businesses across Kent
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {services.map((service, index) => (
              <Link 
                key={service.id} 
                to={`/services#${service.slug}`}
                className="block"
              >
                <Card 
                  className="group hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border-0 bg-white h-full"
                  style={{
                    animationDelay: `${index * 100}ms`
                  }}
                >
                  <div className="relative h-56 overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={`${service.name} service – professional painting and decorating in Kent`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-xl font-bold text-white drop-shadow-lg">{service.name}</h3>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <p className="text-slate-600 leading-relaxed text-sm">
                      {service.shortDescription}
                    </p>
                    <span className="inline-flex items-center text-cyan-600 font-medium mt-4 group-hover:translate-x-1 transition-transform" aria-hidden="true">
                      Learn more <ArrowRight className="ml-1 h-4 w-4" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          <div className="text-center">
            <Link to="/services">
              <Button size="lg" className="bg-cyan-600 hover:bg-cyan-700 text-white px-8 rounded-full transition-all duration-300 hover:scale-105">
                View All Services
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Review Carousel - Moved up for prominence */}
      <ReviewCarousel />

      {/* CTA Section - Between carousel and Checkatrade */}
      <section className="py-20 bg-gradient-to-br from-cyan-600 to-teal-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Transform Your Space?
            </h2>
            <p className="text-xl mb-8 text-cyan-100">
              Get in touch with us today for a free, no-obligation quote. We're here to help bring your vision to life.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-cyan-600 hover:bg-cyan-50 px-8 py-6 text-lg rounded-full transition-all duration-300 hover:scale-105 shadow-xl">
                  Get Free Quote
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/portfolio">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg rounded-full transition-all duration-300 hover:scale-105">
                  View Our Work
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Checkatrade Trust Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-slate-50 to-cyan-50 rounded-3xl p-8 md:p-12 shadow-lg border border-cyan-100">
              <div className="text-center">
                <div className="inline-flex items-center justify-center bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Vetted & Verified Tradesperson
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Trusted on Checkatrade
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
                  Paint It Better is proud to be a <strong>Checkatrade-verified painter and decorator</strong> serving Kent. 
                  Checkatrade is the UK's leading trade directory, where only vetted professionals can join. 
                  Our profile features genuine reviews from real customers who've experienced our work first-hand.
                </p>
                
                <a 
                  href="https://www.checkatrade.com/trades/PaintItBetterLtd" 
                  target="_blank" 
                  rel="noopener"
                  className="inline-flex items-center justify-center bg-cyan-600 hover:bg-cyan-700 text-white px-8 py-4 text-lg font-semibold rounded-full transition-all duration-300 hover:scale-105 shadow-lg"
                >
                  View our Checkatrade reviews and portfolio
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
                <p className="mt-4 text-sm text-slate-500">
                  See what our customers say about our painting and decorating services in Kent
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info Bar */}
      <section className="py-12 bg-slate-900 text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="flex flex-col items-center space-y-2">
              <MapPin className="h-8 w-8 text-cyan-500" />
              <p className="text-sm text-slate-300">Wye, Ashford</p>
              <p className="text-sm text-slate-300">Kent, TN25 5AS</p>
            </div>
            <div className="flex flex-col items-center space-y-2">
              <Mail className="h-8 w-8 text-cyan-500" />
              <a href="mailto:hello@paint-it-better.com" className="text-sm text-slate-300 hover:text-cyan-400 transition-colors">
                hello@paint-it-better.com
              </a>
            </div>
            <div className="flex flex-col items-center space-y-2">
              <Star className="h-8 w-8 text-cyan-500" />
              <p className="text-sm text-slate-300">Find us on</p>
              <a href="http://facebook.com/paint.it.better.uk" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-orange-300 transition-colors">
                Facebook
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;